import type {
  AccountabilityBlockedTask,
  AccountabilityBlockerEntry,
  AccountabilityDriEntry,
  AccountabilityOverview,
  AccountabilityTask,
  AccountabilityUser,
  PriorityStatus,
} from "@/types";

const KNOWN_STATUSES: readonly PriorityStatus[] = [
  "focus",
  "queue",
  "done",
  "blocked",
  "on-hold",
  "waiting",
];

/** Unknown BE status strings would crash the status-chip lookup downstream. */
function parseStatus(raw: unknown, fallback: PriorityStatus): PriorityStatus {
  return KNOWN_STATUSES.includes(raw as PriorityStatus) ? (raw as PriorityStatus) : fallback;
}

function parseUser(raw: unknown): AccountabilityUser | null {
  if (!raw || typeof raw !== "object") return null;
  const u = raw as Record<string, unknown>;
  if (typeof u.pid !== "string" || typeof u.fullName !== "string") return null;
  return {
    pid: u.pid,
    fullName: u.fullName,
    avatarUrl: typeof u.avatarUrl === "string" ? u.avatarUrl : undefined,
    position: typeof u.position === "string" ? u.position : undefined,
  };
}

function parseOwner(raw: unknown): { pid: string; fullName: string } | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.pid !== "string" || typeof o.fullName !== "string") return null;
  return { pid: o.pid, fullName: o.fullName };
}

function parseTask(raw: unknown): AccountabilityTask | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.pid !== "string" || typeof t.code !== "string" || typeof t.name !== "string") return null;
  return {
    pid: t.pid,
    code: t.code,
    name: t.name,
    status: parseStatus(t.status, "queue"),
  };
}

function parseBlockedTask(raw: unknown): AccountabilityBlockedTask | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as Record<string, unknown>;
  if (typeof t.pid !== "string" || typeof t.code !== "string" || typeof t.name !== "string") return null;
  const owner = parseOwner(t.owner);
  if (!owner) return null;
  return {
    pid: t.pid,
    code: t.code,
    name: t.name,
    status: parseStatus(t.status, "blocked"),
    blockedReason: typeof t.blockedReason === "string" ? t.blockedReason : undefined,
    owner,
  };
}

function parseDriEntry(raw: unknown): AccountabilityDriEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Record<string, unknown>;
  const user = parseUser(e.user);
  if (!user) return null;
  const tasks = Array.isArray(e.tasks) ? (e.tasks.map(parseTask).filter(Boolean) as AccountabilityTask[]) : [];
  const taskCount = typeof e.taskCount === "number" ? e.taskCount : tasks.length;
  return { user, taskCount, tasks };
}

function parseBlockerEntry(raw: unknown): AccountabilityBlockerEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Record<string, unknown>;
  const user = parseUser(e.user);
  if (!user) return null;
  const blockedTasks = Array.isArray(e.blockedTasks)
    ? (e.blockedTasks.map(parseBlockedTask).filter(Boolean) as AccountabilityBlockedTask[])
    : [];
  const blockedCount = typeof e.blockedCount === "number" ? e.blockedCount : blockedTasks.length;
  return { user, blockedCount, blockedTasks };
}

/**
 * Normalizes `GET /api/priorities/accountability` into the FE model, dropping any row missing the
 * fields it needs to render rather than throwing — one malformed row should not blank the whole page.
 */
export function parseAccountabilityOverview(raw: unknown): AccountabilityOverview {
  if (!raw || typeof raw !== "object") return { dris: [], blockers: [] };
  let r = raw as Record<string, unknown>;
  // Some BE endpoints wrap payloads in `{data: ...}` (see getPriorities) — accept both shapes.
  if (r.data && typeof r.data === "object" && !Array.isArray(r.data)) {
    r = r.data as Record<string, unknown>;
  }
  const dris = Array.isArray(r.dris) ? (r.dris.map(parseDriEntry).filter(Boolean) as AccountabilityDriEntry[]) : [];
  const blockers = Array.isArray(r.blockers)
    ? (r.blockers.map(parseBlockerEntry).filter(Boolean) as AccountabilityBlockerEntry[])
    : [];
  return { dris, blockers };
}
